import React, {useId} from 'react';
import {useTranslation} from 'react-i18next';
import {adgroupLevelLabel} from '../../utils/channelLevelLabels';
import {platformName} from '../agency/connections_v2/platformsText';
import {
    LEVEL_AD, LEVEL_ADGROUP, MATCH_OPTIONS, availableParams, matchByEnabled, ruleFromPayload,
} from './attributionModel';
import {RAW, capitalizeFirst, paramLabel} from './attributionText';

/**
 * Los tres controles de una regla: en qué parámetro viaja el grupo de anuncios, en cuál el anuncio, y
 * si se cruza por nombre o por ID. `<select>` nativos: andan con el teclado y en el celular abren el
 * selector del sistema.
 *
 * - `layout="row"`: una fila de la tarjeta de la agencia. Los rótulos quedan para el lector de
 *   pantalla (los títulos de las columnas los dicen a la vista) y reaparecen en angosto, donde la fila
 *   se apila. Si la plataforma llama distinto al nivel que el título de la columna («conjunto de
 *   anuncios» en Meta), se dice debajo del control.
 * - `layout="stacked"`: la cuenta del cliente, con los rótulos a la vista.
 *
 * Cada `<select>` lleva `aria-label` con la plataforma: en la tarjeta hay una fila por plataforma y
 * sin ella el lector diría lo mismo en todas.
 *
 * `onChange(field, value)`, con `field` ∈ `adgroup_param | ad_param | match_by` y `null` para «No
 * cruzar».
 */
export function AttributionRuleFields({channel, rule, onChange, disabled = false, layout = 'stacked'}) {
    const {t} = useTranslation();
    const baseId = useId();
    const current = ruleFromPayload(rule);
    const level = adgroupLevelLabel(t, channel);
    const platform = platformName(channel);
    const matchEnabled = matchByEnabled(current);
    const caption = layout === 'row' && level !== adgroupLevelLabel(t, null) ? capitalizeFirst(level) : null;

    const labels = {
        adgroup_param: t('ga4_attribution.field.adgroup_travels', {level, interpolation: RAW}),
        ad_param: t('ga4_attribution.field.ad_travels'),
        match_by: t('ga4_attribution.field.match_by'),
    };
    const ariaLabel = (field) => `${platform} · ${labels[field]}`;

    const paramSelect = (field, levelKey) => (
        <div className="ga4-attr-field" key={field}>
            <label className="ga4-attr-field__label" htmlFor={`${baseId}-${field}`}>{labels[field]}</label>
            <select id={`${baseId}-${field}`} className="ga4-attr-field__select" aria-label={ariaLabel(field)}
                    value={current[field] || ''} disabled={disabled}
                    onChange={(event) => onChange(field, event.target.value || null)}>
                {availableParams(levelKey, current).map(({param, disabled: taken}) => (
                    <option key={param || 'none'} value={param || ''} disabled={taken}>{paramLabel(t, param)}</option>
                ))}
            </select>
            {field === 'adgroup_param' && caption && <span className="ga4-attr-field__caption">{caption}</span>}
        </div>
    );

    return (
        <div className={`ga4-attr-fields -${layout}`}>
            {paramSelect('adgroup_param', LEVEL_ADGROUP)}
            {paramSelect('ad_param', LEVEL_AD)}
            <div className="ga4-attr-field">
                <label className="ga4-attr-field__label" htmlFor={`${baseId}-match_by`}>{labels.match_by}</label>
                {/* Sin ningún nivel cruzado no hay nada que cruzar: el control queda apagado y vacío,
                    no mostrando un «Nombre» que no significa nada. */}
                <select id={`${baseId}-match_by`} className="ga4-attr-field__select" aria-label={ariaLabel('match_by')}
                        value={matchEnabled ? current.match_by : ''} disabled={disabled || !matchEnabled}
                        onChange={(event) => onChange('match_by', event.target.value)}>
                    {!matchEnabled && <option value="">—</option>}
                    {MATCH_OPTIONS.map((option) => (
                        <option key={option} value={option}>{t(`ga4_attribution.match.${option}`)}</option>
                    ))}
                </select>
            </div>
        </div>
    );
}

export default AttributionRuleFields;
